import React, { useState, useEffect } from 'react';
import { Box, Typography, Grid, CircularProgress } from '@mui/material';
import { NAVY, ORANGE, PageHeader, BandChip, UseWithCareBanner, GuidanceBanner, MarkerBar, bandColor, riskBandColor, RISK_BAND_COLORS } from '../components/widgets.comp';
import RiskSelector from '../components/RiskSelector.comp';
import CircularLoader from '../../../components/CircularLoader';
import NoDataAvailable from '../../../components/NoDataAvailable';
import { useDashboard } from '../../../context/DashboardContext';
import {isSuppressedItem, whole} from '../../DashboardCommon/dashboardCards';
import {SuppressedValue} from '../../DashboardCommon/SuppressedValue.comp';

const LEGEND = Object.keys(RISK_BAND_COLORS);

const CYBER_RISK_BY_AGE_SECTION_CODE = 'S_10';
const CARD_SECTION = CYBER_RISK_BY_AGE_SECTION_CODE;

const CyberRiskByAgeTab = () => {
    const { dashboardData, loading } = useDashboard({ card_sections: CARD_SECTION });
    const [selectedRisk, setSelectedRisk] = useState('');

    // 1. Safely locate the S_10 section
    const s10Section = dashboardData?.cards?.sections?.find(
        (section) => section.section_code === CYBER_RISK_BY_AGE_SECTION_CODE
    );

    const cards = s10Section?.cards || [];

    // Extract dynamic TitleCard properties
    const titleCard = cards.find(c => c.card_type === 'TitleCard');
    const headerTitle = titleCard?.title || 'Cyber Risk by Age';
    const headerSubtitle = titleCard?.footer || 'Are the rates similar across ages, or does a subgroup need support?';

    // Extract LeftLineCard properties
    const leftLineCard = cards.find(c => c.card_type === 'LeftLineCard');
    let useWithCareText = 'Read group differences as a prompt for support, not a judgement about students. Groups under 10 students are suppressed.';
    if (leftLineCard?.leftline_html) {
        const cleaned = leftLineCard.leftline_html
            .replace(/<p>/gi, '')
            .replace(/<\/p>/gi, '')
            .replace(/<strong>Use with care\.?<\/strong>/gi, '')
            .trim();
        if (cleaned) {
            useWithCareText = cleaned;
        }
    }

    // Extract risks list and chart cards
    const listCard = cards.find(c => c.card_type === 'ListCard');
    const barChartCard = cards.find(c => c.card_type === 'BarChartBMCard');

    // Build risk option objects dynamically from listCard or barChartCard items
    const riskOptionObjects = React.useMemo(() => {
        const map = new Map();

        const registerRisk = (name, parent_label, sublabel) => {
            if (!name) return;
            const cleanName = name.trim();
            const key = cleanName.toLowerCase();
            if (!map.has(key)) {
                map.set(key, { name: cleanName, parent_label, sublabel });
            } else if (parent_label || sublabel) {
                const existing = map.get(key);
                if (!existing.parent_label && parent_label) existing.parent_label = parent_label;
                if (!existing.sublabel && sublabel) existing.sublabel = sublabel;
            }
        };

        if (listCard?.list_objects?.length > 0) {
            listCard.list_objects.forEach(obj => {
                registerRisk(obj.name || obj.label, obj.parent_label, obj.sublabel);
            });
        }

        if (barChartCard?.items?.length > 0) {
            barChartCard.items.forEach(item => {
                let riskName = item.label || '';
                if (riskName.includes(' · ')) {
                    riskName = riskName.split(' · ')[0];
                } else if (riskName.includes('·')) {
                    riskName = riskName.split('·')[0];
                } else if (riskName.includes(' - ')) {
                    riskName = riskName.split(' - ')[0];
                }
                registerRisk(riskName, item.parent_label, item.sublabel);
            });
        }

        return Array.from(map.values());
    }, [listCard, barChartCard]);

    const riskOptions = React.useMemo(() => riskOptionObjects.map(o => o.name), [riskOptionObjects]);

    // Determine which options are not available in C_4 items (matching exact or as prefix)
    const disabledOptions = riskOptions.filter(option => {
        const optionLower = option.toLowerCase();
        const hasMatch = barChartCard?.items?.some(item => {
            const labelLower = item.label?.toLowerCase() || '';
            return labelLower === optionLower || 
                   labelLower.startsWith(optionLower + ' ·') || 
                   labelLower.startsWith(optionLower + '·') ||
                   labelLower.startsWith(optionLower + ' -') ||
                   labelLower.startsWith(optionLower + '-');
        });
        return !hasMatch;
    });

    // Keep state in sync with the loaded options, ensuring a non-disabled option is selected by default
    useEffect(() => {
        const enabledOptions = riskOptions.filter(o => !disabledOptions.includes(o));
        if (enabledOptions.length > 0) {
            if (!selectedRisk || !enabledOptions.includes(selectedRisk)) {
                setSelectedRisk(enabledOptions[0]);
            }
        } else if (riskOptions.length > 0 && !selectedRisk) {
            setSelectedRisk(riskOptions[0]);
        }
    }, [riskOptions, disabledOptions, selectedRisk]);

    if (loading) {
        return <CircularLoader minHeight="200px" />;
    }

    if (!s10Section) {
        return (
            <NoDataAvailable />
        );
    }

    // Find all items matching our selection (exact match or prefix)
    const matchingItems = barChartCard?.items?.filter(item => {
        const labelLower = item.label?.toLowerCase() || '';
        const selectedRiskLower = selectedRisk?.toLowerCase() || '';
        return labelLower === selectedRiskLower || 
               labelLower.startsWith(selectedRiskLower + ' ·') || 
               labelLower.startsWith(selectedRiskLower + '·') ||
               labelLower.startsWith(selectedRiskLower + ' -') ||
               labelLower.startsWith(selectedRiskLower + '-');
    }) || [];

    // Parse flat matching items into age rows
    let rows = [];

    if (matchingItems.length > 0) {
        rows = matchingItems.map((item) => {
            // Extract the age identifier
            let ageVal = item.dims?.age || item.dims?.Age;
            if (!ageVal) {
                const parts = item.label.split(/[·•-]/);
                if (parts.length > 1) {
                    ageVal = parts[parts.length - 1].trim();
                }
            }
            // Prefix label with "Age" if it is numeric, e.g. "10" -> "Age 10"
            const labelText = ageVal ? (ageVal.toLowerCase().includes('age') ? ageVal : `Age ${ageVal}`) : '';

            const suppressed = isSuppressedItem(item);           // top-level flag OR data.suppressed (older payloads)
            return {
                label: labelText,
                pct: suppressed ? null : whole(item.value),
                band: suppressed ? null : (item.band || null),
                suppressed,
            };
        });
    }


    return (
        <Box>
            <PageHeader
                title={headerTitle}
                subtitle={headerSubtitle}
            />

            <UseWithCareBanner text={useWithCareText}/>

            <Grid container spacing={2.5} alignItems="stretch">
                <Grid item xs={12} md={4}>
                    <RiskSelector 
                        options={riskOptionObjects} 
                        selected={selectedRisk} 
                        onChange={setSelectedRisk} 
                        disabledOptions={disabledOptions}
                    />
                </Grid>
                <Grid item xs={12} md={8}>
                    <Box sx={{border: '1px solid var(--brand-border, #E5E7EB)', borderRadius: '12px', backgroundColor: 'var(--brand-surface, #fff)', boxShadow: '0 2px 8px rgba(0,0,0,0.04)', p: 3, height: '100%'}}>
                        <Box sx={{display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2.5, flexWrap: 'wrap', gap: 1}}>
                            <Box>
                                <Typography sx={{fontSize: '15px', fontWeight: 700, color: NAVY}}>
                                    {selectedRisk}
                                </Typography>
                                <Typography sx={{fontSize: '12px', color: '#6A7282', mt: 0.3}}>
                                    Percentage of students who experienced cyber risk in the past year
                                </Typography>
                            </Box>
                            <Box sx={{display: 'flex', gap: 1, flexWrap: 'wrap'}}>
                                {LEGEND.map((l) => <BandChip key={l} label={l} color={riskBandColor(l)}/>)}
                            </Box>
                        </Box>
                        {rows.map((r, index) => {
                            return r.suppressed ? (
                                <Box key={r.label || index} sx={{mb: 2.2}}><SuppressedValue label={r.label}/></Box>
                            ) : (
                                <Box key={r.label || index} sx={{display: 'flex', alignItems: 'center', gap: 2, mb: 2.2}}>
                                    {r.label && (
                                        <Typography sx={{fontSize: '13.5px', fontWeight: 700, color: NAVY, width: 64, flexShrink: 0}}>
                                            {r.label}
                                        </Typography>
                                    )}
                                    <MarkerBar
                                        score={r.pct}
                                        color={riskBandColor(r.band)}
                                        height={18}
                                        unit="percent"
                                    />
                                    <Typography sx={{fontSize: '14px', fontWeight: 800, color: NAVY, width: 40, textAlign: 'right', flexShrink: 0}}>{r.pct === null ? '—' : `${r.pct}%`}</Typography>
                                    {/* A 0% bar has no width, so the chip carries the band colour (SD slide 72). */}
                                    <Box sx={{flexShrink: 0}}>{r.band && <BandChip label={r.band} color={riskBandColor(r.band)}/>}</Box>
                                </Box>
                            );
                        })}
                    </Box>
                </Grid>
            </Grid>


            {/* Neutral guidance: the old fixed sentence claimed older students carry more risk even when every age group was suppressed. */}
            <GuidanceBanner text="Compare age groups only where every group is shown; focus screen-time and safety support where Priority Concern or Critical appears."/>
        </Box>
    );
};

export default CyberRiskByAgeTab;

